import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Clock, Plus, Trash2, Save, Loader2 } from "lucide-react";

const translations = {
  en: {
    title: "Availability Settings",
    subtitle: "Set your working hours for meeting scheduling",
    timezone: "Timezone",
    currentTimezone: "Your current timezone",
    weeklySchedule: "Weekly Schedule",
    weeklyScheduleDesc: "Set your available hours for each day",
    applyToAll: "Apply to all days",
    addSlot: "Add slot",
    to: "to",
    saveAvailability: "Save Availability",
    saving: "Saving...",
    saved: "Availability Updated",
    savedDesc: "Your availability has been saved",
    error: "Error",
    errorDesc: "Failed to save availability"
  },
  fr: {
    title: "Paramètres de disponibilité",
    subtitle: "Définissez vos heures de travail pour la planification des réunions",
    timezone: "Fuseau horaire",
    currentTimezone: "Votre fuseau horaire actuel",
    weeklySchedule: "Horaire hebdomadaire",
    weeklyScheduleDesc: "Définissez vos heures disponibles pour chaque jour",
    applyToAll: "Appliquer à tous les jours",
    addSlot: "Ajouter un créneau",
    to: "à",
    saveAvailability: "Enregistrer la disponibilité",
    saving: "Enregistrement...",
    saved: "Disponibilité mise à jour",
    savedDesc: "Votre disponibilité a été enregistrée",
    error: "Erreur",
    errorDesc: "Échec de l'enregistrement de la disponibilité"
  }
};

const DAYS = [
  { value: 'monday', labelEn: 'Monday', labelFr: 'Lundi' },
  { value: 'tuesday', labelEn: 'Tuesday', labelFr: 'Mardi' },
  { value: 'wednesday', labelEn: 'Wednesday', labelFr: 'Mercredi' },
  { value: 'thursday', labelEn: 'Thursday', labelFr: 'Jeudi' },
  { value: 'friday', labelEn: 'Friday', labelFr: 'Vendredi' },
  { value: 'saturday', labelEn: 'Saturday', labelFr: 'Samedi' },
  { value: 'sunday', labelEn: 'Sunday', labelFr: 'Dimanche' }
] as const;

interface TimeRange {
  start: string;
  end: string;
}

interface DaySchedule {
  day: string;
  enabled: boolean;
  slots: TimeRange[];
}

interface WeeklyAvailability {
  timezone: string;
  schedule: DaySchedule[];
}

export function AvailabilitySettings() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;
  
  const [availability, setAvailability] = useState<WeeklyAvailability>({
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    schedule: DAYS.map(day => ({
      day: day.value,
      enabled: day.value !== 'saturday' && day.value !== 'sunday',
      slots: [{ start: '09:00', end: '17:00' }]
    }))
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadAvailability();
  }, [user]);

  const loadAvailability = async () => {
    if (!user?.email) return;

    try {
      const availabilityDoc = await getDoc(doc(db, "userAvailability", user.email));

      if (availabilityDoc.exists()) {
        setAvailability(availabilityDoc.data() as WeeklyAvailability);
      }
    } catch (error) {
      console.error("Error loading availability:", error);
    }
  };

  const saveAvailability = async () => {
    if (!user?.email) return;

    setIsSaving(true);
    try {
      await setDoc(doc(db, "userAvailability", user.email), availability);

      toast({
        title: t.saved,
        description: t.savedDesc
      });
    } catch (error) {
      console.error("Error saving availability:", error);
      toast({
        title: t.error,
        description: t.errorDesc,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDay = (dayIndex: number) => {
    const newSchedule = [...availability.schedule];
    newSchedule[dayIndex].enabled = !newSchedule[dayIndex].enabled;
    setAvailability({ ...availability, schedule: newSchedule });
  };

  const updateTimeSlot = (dayIndex: number, slotIndex: number, field: 'start' | 'end', value: string) => {
    const newSchedule = [...availability.schedule];
    newSchedule[dayIndex].slots[slotIndex][field] = value;
    setAvailability({ ...availability, schedule: newSchedule });
  };

  const addTimeSlot = (dayIndex: number) => {
    const newSchedule = [...availability.schedule];
    newSchedule[dayIndex].slots.push({ start: '09:00', end: '17:00' });
    setAvailability({ ...availability, schedule: newSchedule });
  };

  const removeTimeSlot = (dayIndex: number, slotIndex: number) => {
    const newSchedule = [...availability.schedule];
    newSchedule[dayIndex].slots.splice(slotIndex, 1);
    setAvailability({ ...availability, schedule: newSchedule });
  };

  const applyToAllDays = (sourceSchedule: DaySchedule) => {
    const newSchedule = availability.schedule.map(day => ({
      ...day,
      slots: [...sourceSchedule.slots]
    }));
    setAvailability({ ...availability, schedule: newSchedule });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">{t.title}</h2>
        <p className="text-sm text-muted-foreground">{t.subtitle}</p>
      </div>

      {/* Timezone */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t.timezone}</CardTitle>
          <CardDescription>{t.currentTimezone}</CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            value={availability.timezone}
            onChange={(e) => setAvailability({ ...availability, timezone: e.target.value })}
            className="max-w-md"
          />
        </CardContent>
      </Card>

      {/* Weekly Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t.weeklySchedule}</CardTitle>
          <CardDescription>{t.weeklyScheduleDesc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {availability.schedule.map((daySchedule, dayIndex) => (
            <div key={daySchedule.day} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={daySchedule.enabled}
                    onCheckedChange={() => toggleDay(dayIndex)}
                  />
                  <Label className="font-medium capitalize">
                    {language === 'fr' ? DAYS[dayIndex].labelFr : DAYS[dayIndex].labelEn}
                  </Label>
                </div>
                {daySchedule.enabled && (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => applyToAllDays(daySchedule)}
                    >
                      {t.applyToAll}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addTimeSlot(dayIndex)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {t.addSlot}
                    </Button>
                  </div>
                )}
              </div>

              {daySchedule.enabled && (
                <div className="space-y-2">
                  {daySchedule.slots.map((slot, slotIndex) => (
                    <div key={slotIndex} className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <Input
                        type="time"
                        value={slot.start}
                        onChange={(e) => updateTimeSlot(dayIndex, slotIndex, 'start', e.target.value)}
                        className="w-32"
                      />
                      <span className="text-muted-foreground">{t.to}</span>
                      <Input
                        type="time"
                        value={slot.end}
                        onChange={(e) => updateTimeSlot(dayIndex, slotIndex, 'end', e.target.value)}
                        className="w-32"
                      />
                      {daySchedule.slots.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeTimeSlot(dayIndex, slotIndex)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveAvailability} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t.saving}
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              {t.saveAvailability}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
